import PendoDraggable, { shouldCancelDrag } from '../directives/draggable/pendo-draggable.js';
import { emitEvent } from '../components/table/utils/utils.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _invoke(body, then) {
  var result = body();
  if (result && result.then) {
    return result.then(then);
  }
  return then(result);
}
function _empty() {}
function _invokeIgnored(body) {
  var result = body();
  if (result && result.then) {
    return result.then(_empty);
  }
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var tableDraggableMixin = {
  directives: {
    PendoDraggable
  },
  data() {
    return {
      tableComponentKey: 0,
      draggedTableRow: null,
      sourceUID: null,
      lastMirrorX: null,
      lastMirrorY: null
    };
  },
  watch: {
    '$table.draggable': {
      handler() {
        this.tableComponentKey++;
      }
    }
  },
  methods: {
    mirrorCreateHandler(event) {
      event.cancel();
    },
    dragStartHandler(event) {
      const {
        data: {
          source,
          sensorEvent
        }
      } = event;
      if (shouldCancelDrag(sensorEvent.target)) {
        return event.cancel();
      }
      const preExistingMirror = document.querySelector("tr[pendo-draggable-mirror='']");
      if (preExistingMirror) {
        preExistingMirror.remove();
        return event.cancel();
      }
      const sourceActions = source.querySelector('td.pendo-table__column--actions .pendo-table__cell');
      while (sourceActions && sourceActions.hasChildNodes()) {
        sourceActions.removeChild(sourceActions.lastChild);
      }
      const tableContainer = document.querySelector(`#pendo-table-${this.$table.id}`);
      const {
        right,
        width,
        bottom,
        height
      } = source.getBoundingClientRect();
      const y = bottom - height - sensorEvent.clientY;
      const x = right - width - sensorEvent.clientX;
      const mirror = source.cloneNode(true);
      mirror.setAttribute('pendo-draggable-mirror', '');
      mirror.removeAttribute('pendo-draggable-item');
      Object.assign(mirror.style, {
        'display': 'none',
        'position': 'fixed',
        'pointerEvents': 'none',
        'top': `${y}px`,
        'left': `${x}px`,
        'vertical-align': 'middle',
        'boxShadow': '0px 10px 16px rgba(0, 0, 0, 0.17)',
        'z-index': '999999'
      });
      const fixedMirrorColumns = mirror.querySelectorAll('td.pendo-table__column--fixed-left') || [];
      fixedMirrorColumns.forEach(mirrorColumn => {
        Object.assign(mirrorColumn.style, {
          left: `${x - this.$table.scrollStore.scrollLeft}px`
        });
      });
      const tbodyContainer = document.querySelector(`#pendo-table-${this.$table.id} tbody`);
      this.draggedTableRow = {
        id: Array.from(tbodyContainer.querySelectorAll('tr')).findIndex(node => {
          return node.getAttribute('data-uid') === mirror.getAttribute('data-uid');
        }),
        index: parseInt(mirror.getAttribute('data-uid'))
      };
      emitEvent(this.$table, 'table-row-drag-start', [{
        event,
        from: mirror.getAttribute('data-uid')
      }]);
      tableContainer.appendChild(mirror);
    },
    dragMoveHandler(event) {
      const {
        data: {
          sensorEvent
        }
      } = event;
      const mirror = document.querySelector("tr[pendo-draggable-mirror='']");
      this.$table.clearRowHover();
      if (mirror) {
        requestAnimationFrame(() => {
          const translateY = Math.round(sensorEvent.clientY);
          const translateX = Math.round(sensorEvent.clientX);
          this.lastMirrorY = translateY;
          this.lastMirrorX = translateX;
          mirror.style.display = 'block';
          mirror.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px)`;
        });
      }
    },
    dragOverHandler(event) {
      const {
        data: {
          over
        }
      } = event;
      if (!over.classList.contains('pendo-draggable-source--is-dragging')) {
        const overUID = parseInt(over.getAttribute('data-uid'));
        const prevUID = over.previousElementSibling ? parseInt(over.previousElementSibling.getAttribute('data-uid')) : null;
        const nextUID = over.nextElementSibling ? parseInt(over.nextElementSibling.getAttribute('data-uid')) : null;
        if (this.sourceUID === null) {
          this.sourceUID = overUID;
        } else if (!over.previousElementSibling) {
          this.sourceUID = 0;
        } else if (!over.nextElementSibling) {
          this.sourceUID = this.$table.tableData.length - 1;
        } else if (overUID === this.sourceUID) {
          this.sourceUID = prevUID < overUID ? prevUID : nextUID;
        } else {
          this.sourceUID = overUID;
        }
        emitEvent(this.$table, 'table-row-drag-over', [{
          event,
          to: this.sourceUID
        }]);
      }
    },
    dragStopHandler: _async(function (event) {
      const _this = this;
      const {
        data: {
          source
        }
      } = event;
      const mirror = document.querySelector("tr[pendo-draggable-mirror='']");
      return _invokeIgnored(function () {
        if (mirror) {
          const {
            x: sourceX,
            y: sourceY
          } = source.getBoundingClientRect();
          const {
            x: mirrorX,
            y: mirrorY
          } = mirror.getBoundingClientRect();
          mirror.addEventListener('transitionend', _this.dragTransitionEndHandler.bind(_this, {
            mirror
          }));
          requestAnimationFrame(() => {
            mirror.style.transition = 'transform 80ms ease-in-out, box-shadow 80ms ease-in-out';
            mirror.style.transform = `translate3d(${_this.lastMirrorX + sourceX - mirrorX}px, ${_this.lastMirrorY + sourceY - mirrorY}px, 0)`;
            mirror.style.boxShadow = '0px 0px 0px rgba(0, 0, 0, 0.17)';
            setTimeout(() => {
              _this.dragTransitionEndHandler({
                mirror
              });
            }, 100);
          });
          _this.draggedTableRow = null;
          const initialUID = mirror.getAttribute('data-uid');
          return _invoke(function () {
            if (_this.sourceUID !== null && initialUID !== null) {
              const tempTable = _this.$table.tableData.slice();
              tempTable.splice(_this.sourceUID, 0, tempTable.splice(initialUID, 1)[0]);
              return _await(_this.$table.loadData(tempTable), function () {
                _this.tableComponentKey++;
              });
            }
          }, function () {
            emitEvent(_this.$table, 'table-row-drag-stop', [{
              event,
              from: initialUID,
              to: _this.sourceUID,
              tableData: _this.$table.tableData
            }]);
          });
        }
      });
    }),
    dragTransitionEndHandler(_ref) {
      let {
        mirror
      } = _ref;
      if (mirror) {
        mirror.removeEventListener('transitionend', this.dragTransitionEndHandler);
        mirror.remove();
        this.sourceUID = null;
        this.lastMirrorY = null;
        this.lastMirrorX = null;
      }
    }
  }
};

export { tableDraggableMixin as default };
//# sourceMappingURL=table-draggable.js.map
